import { useState, useEffect, useMemo, useCallback } from "react";
import axiosInstance from "../../utils/axiosInstance";
import logoNormal from "../../assets/images/logo_transparent_bg.png";
import logoDark from "../../assets/images/logo-dark.png";
import McbLogo from "../../assets/images/logos/mcb-logo.svg";
import DibLogo from "../../assets/images/logos/dibLogo.png";
import "./Overview.css";
import LoaderComponent from "../../components/Spinner/LoaderComponent";
import Toast from "../../components/Toast/Toast";
import { QUICK_LINKS_CONFIG } from "./quick-link-list";

// Components
import TileInfo from "./tile-info";
import BranchAtmTable from "./branch-atm-table";
import QuickLinks from "./quick-links";
import RestrictedUserSummary from "./restricted-user-summary";

// Constants
const LOGO_MAP = {
  dib: DibLogo,
  MCB: McbLogo,
};

// Custom hooks (keep the same as your original code)
const useUserData = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(`/users/profile`);

        if (response.message.status !== "200") {
          throw new Error(response.message.description);
        }

        const user = response.data.users[0];
        setUserData(user);
      } catch (err) {
        Toast(
          err.response?.data?.message?.description || "Error loading user data",
          "error"
        );
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  return { userData, loading, error };
};

const useUserPermissions = () => {
  return useMemo(() => {
    const roles = JSON.parse(localStorage.getItem("userRoles"))?.data?.roles;
    if (!roles) return [];

    return roles
      .flatMap((role) => role.permissions || [])
      .map((permission) => permission?.id)
      .filter(Boolean);
  }, []);
};

const useDynamicMenus = () => {
  const [dynamicMenus, setDynamicMenus] = useState([]);
  const [accountsList, setAccountsList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDynamicData = async () => {
      try {
        // Fetch dynamic recon menus
        const reconResponse = await axiosInstance.get("/recon/recon-menus");
        if (
          reconResponse.message?.status === "200" ||
          reconResponse.status === 200
        ) {
          setDynamicMenus(reconResponse.data || []);
        }

        // Fetch accounts menu
        const accountsResponse = await axiosInstance.get(
          "/accounts/account-types?typeIndicator=gl"
        );
        if (accountsResponse.data) {
          setAccountsList(accountsResponse.data || []);
        }
      } catch (err) {
        console.log("Error fetching dynamic menus:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDynamicData();
  }, []);

  return { dynamicMenus, accountsList, loading };
};

const useFilteredQuickLinks = (permissions, dynamicMenus, accountsList) => {
  return useMemo(() => {
    if (!permissions.length) return [];

    const permissionSet = new Set(permissions);
    const quickLinks = [];

    // Process static menu items - only show if user has main menu permission
    QUICK_LINKS_CONFIG.forEach((group) => {
      // Check if user has permission for the main menu
      if (permissionSet.has(group.mainPermission)) {
        // Filter submenu items based on permissions
        const filteredItems = group.items.filter((item) =>
          permissionSet.has(item.permission)
        );

        // Only add the group if there are visible submenu items
        if (filteredItems.length > 0) {
          quickLinks.push({
            ...group,
            items: filteredItems,
          });
        }
      }
    });

    // Add dynamic recon menus if available - each group as a separate menu
    if (dynamicMenus.length > 0) {
      dynamicMenus.forEach((group) => {
        // Check if user has permission for the main dynamic menu
        const groupPermission = group?.groupPermission?.id;
        if (groupPermission && permissionSet.has(groupPermission)) {
          const reconItems = [];

          group.recons.forEach((recon) => {
            const menuPermission = recon.permission?.find(
              (item) => item.type === "menu"
            )?.id;
            if (menuPermission && permissionSet.has(menuPermission)) {
              reconItems.push({
                title: recon.reconName,
                path: `/dynamic-recon`,
                state: { subItem: recon, item: group },
                permission: menuPermission,
              });
            }
          });

          // Only add the group if there are visible submenu items
          if (reconItems.length > 0) {
            quickLinks.push({
              group: group.groupName,
              mainPermission: groupPermission,
              items: reconItems,
            });
          }
        }
      });
    }

    // Add accounts menu if user has main accounts permission
    if (accountsList.length > 0 && permissionSet.has("mnu_acc")) {
      const accountItems = accountsList.map((account) => ({
        title: account.accountType,
        path: `/Accounts?typeId=${account.accountId}`,
        permission: "mnu_sub_acc",
      }));

      // Only add accounts if user has sub-account permission
      const filteredAccountItems = accountItems.filter((item) =>
        permissionSet.has(item.permission)
      );

      if (filteredAccountItems.length > 0) {
        quickLinks.push({
          group: "Accounts",
          mainPermission: "mnu_acc",
          items: filteredAccountItems,
        });
      }
    }

    return quickLinks;
  }, [permissions, dynamicMenus, accountsList]);
};

// Main Overview Component
const Overview = () => {
  const [expandedGroups, setExpandedGroups] = useState({});
  const [showUserDetails, setShowUserDetails] = useState(false);

  // Fetch user data from API
  const { userData, loading, error } = useUserData();
  const theme = localStorage.getItem("theme") || "light";
  const logoType = localStorage.getItem("landingPageLogo");

  // Get dynamic menus and accounts
  const {
    dynamicMenus,
    accountsList,
    loading: dynamicLoading,
  } = useDynamicMenus();

  // Get user permissions and filtered quick links
  const permissions = useUserPermissions();
  const quickLinks = useFilteredQuickLinks(
    permissions,
    dynamicMenus,
    accountsList
  );

  // Initialize expanded groups state
  useEffect(() => {
    const initialExpandedState = quickLinks.reduce((acc, _, index) => {
      acc[index] = false;
      return acc;
    }, {});
    setExpandedGroups(initialExpandedState);
  }, [quickLinks]);

  // Toggle group expansion handler
  const toggleGroup = useCallback((groupIndex) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupIndex]: !prev[groupIndex],
    }));
  }, []);

  // Toggle user details visibility
  const toggleUserDetails = useCallback(() => {
    setShowUserDetails((prev) => !prev);
  }, []);

  // Determine logo based on type and theme
  const logo = useMemo(() => {
    if (logoType && LOGO_MAP[logoType]) {
      return LOGO_MAP[logoType];
    }
    return theme === "dark" ? logoDark : logoNormal;
  }, [logoType, theme]);

  // Get user role name
  const userRole = userData?.roles?.[0]?.name || "N/A";

  // Loading state
  if (loading || dynamicLoading) {
    return (
      <div className="rs-general-wrapper overview-container">
        <div className="overview-loading">
          <LoaderComponent />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="rs-general-wrapper overview-container">
        <div className="overview-error">
          <h3>Error Loading User Data</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const isRestrictedUser = userData?.restrictedUser;

  return (
    <div className="rs-general-wrapper overview-container">
      {/* Header Section */}
      {/* <div className="overview-header">
        <img src={logo} alt="reconSTREAM Logo" className="overview-logo" />
      </div> */}

      {/* User Information Section */}
      <div className="overview-user-section">
        <div className="card-primary user-profile-card pb-1 pb-3">
          {/* Card Header with Toggle for Restricted Users */}
          <div
            className={`user-card-header ${
              !isRestrictedUser ? "d-flex justify-content-center" : ""
            }`}
          >
            <div className="user-greeting">
              <h2 className="user-name color-dark">
                Welcome, {userData?.firstName} {userData?.lastName}
                <span className="role-badge">{userRole}</span>
              </h2>
            </div>

            {/* Show toggle button only for restricted users */}
            {isRestrictedUser && (
              <button
                className="details-toggle-btn"
                onClick={toggleUserDetails}
                aria-label={showUserDetails ? "Hide details" : "Show details"}
              >
                <span className="toggle-text">
                  {showUserDetails ? "Hide Details" : "More Details"}
                </span>
              </button>
            )}
          </div>

          {/* Collapsible Content for Restricted Users */}
          {showUserDetails && (
            <div className="user-info-content">
              {/* Tile Info Component */}
              <TileInfo userData={userData} />

              {/* Restricted User Summary Alert */}
              {isRestrictedUser && (
                <RestrictedUserSummary userData={userData} />
              )}

              {/* Branch and ATM Table Component */}
              {isRestrictedUser && userData?.branchDetails?.length > 0 && (
                <BranchAtmTable userData={userData} />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Quick Links Section - Always visible */}
      <QuickLinks
        quickLinks={quickLinks}
        expandedGroups={expandedGroups}
        onToggleGroup={toggleGroup}
      />
    </div>
  );
};

export default Overview;
